import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import './LoginPage.css';
import { LOGIN_ROLE_OPTIONS } from '../../../constants/roles';
import AppLogo from '../../../components/common/AppLogo';

const ERROR_DISPLAY_MS = 3000;
const LOGIN_ERROR_STORAGE_KEY = 'shm_login_error';
const MAX_FAILED_PASSWORD_ATTEMPTS = 3;

/* OTP login — restore when email verification is re-enabled
const OTP_LENGTH = 6;
const OTP_LOCKOUT_KEY_PREFIX = 'shm_otp_lockout_';

const getOtpLockKey = (email) => `${OTP_LOCKOUT_KEY_PREFIX}${email.trim().toLowerCase()}`;

const formatLockoutMessage = (untilMs) => {
  const remainingMs = untilMs - Date.now();
  if (remainingMs <= 0) return '';
  const minutes = Math.max(1, Math.ceil(remainingMs / 60000));
  return `Too many failed OTP attempts. Please wait ${minutes} minute${minutes === 1 ? '' : 's'} before trying again.`;
};

const readStoredLockout = (key) => {
  const stored = sessionStorage.getItem(key);
  if (!stored) return null;
  const untilMs = Number(stored);
  if (!untilMs || untilMs <= Date.now()) {
    sessionStorage.removeItem(key);
    return null;
  }
  return untilMs;
};

const maskEmail = (email) => {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  const visible = local.slice(0, Math.min(2, local.length));
  return `${visible}${'*'.repeat(Math.max(local.length - visible.length, 1))}@${domain}`;
};
*/

export default function LoginPage() {
  const { login } = useAuth();
  // OTP login — restore: const { requestLoginOtp, verifyLoginOtp } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', role: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [failedPasswordAttempts, setFailedPasswordAttempts] = useState(0);
  const errorTimerRef = useRef(null);

  /* OTP login — restore when email verification is re-enabled
  const [step, setStep] = useState('credentials');
  const [otp, setOtp] = useState('');
  const [info, setInfo] = useState('');
  const [otpLockedUntil, setOtpLockedUntil] = useState(null);
  const lockoutTimerRef = useRef(null);
  const isOtpLocked = otpLockedUntil && otpLockedUntil > Date.now();

  const persistOtpLockout = useCallback((email, lockedUntilIso) => {
    const untilMs = new Date(lockedUntilIso).getTime();
    if (!untilMs || untilMs <= Date.now()) return;

    sessionStorage.setItem(getOtpLockKey(email), String(untilMs));
    setOtpLockedUntil(untilMs);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    setInfo('');
    setError(formatLockoutMessage(untilMs));
  }, []);

  const clearOtpLockout = useCallback((email) => {
    sessionStorage.removeItem(getOtpLockKey(email));
    setOtpLockedUntil(null);
  }, []);
  */

  const showError = useCallback((message) => {
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    setError(message);
    const duration = /attempt/i.test(message) ? 8000 : ERROR_DISPLAY_MS;
    errorTimerRef.current = setTimeout(() => {
      setError('');
      errorTimerRef.current = null;
    }, duration);
  }, []);

  /* OTP login — restore when email verification is re-enabled
  const showError = useCallback((message) => {
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    setInfo('');
    setError(message);
    const isLockoutMessage = /too many failed otp attempts/i.test(message);
    if (isLockoutMessage) return;
    const duration = /attempt/i.test(message) ? 8000 : ERROR_DISPLAY_MS;
    errorTimerRef.current = setTimeout(() => {
      setError('');
      errorTimerRef.current = null;
    }, duration);
  }, []);

  const handleOtpFailure = useCallback((err, email) => {
    if (err.lockedUntil) {
      persistOtpLockout(email, err.lockedUntil);
      return;
    }
    showError(err.message || 'Invalid or expired verification code.');
  }, [persistOtpLockout, showError]);
  */

  useEffect(() => {
    const stored = sessionStorage.getItem(LOGIN_ERROR_STORAGE_KEY);
    if (stored) {
      sessionStorage.removeItem(LOGIN_ERROR_STORAGE_KEY);
      showError(stored);
    }
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, [showError]);

  useEffect(() => {
    setFailedPasswordAttempts(0);
  }, [form.email]);

  /* OTP login — restore when email verification is re-enabled
  useEffect(() => {
    const email = form.email.trim().toLowerCase();
    if (!email) {
      setOtpLockedUntil(null);
      return;
    }

    const otpLock = readStoredLockout(getOtpLockKey(email));
    setOtpLockedUntil(otpLock);

    if (otpLock && (step === 'otp' || step === 'credentials')) {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      setInfo('');
      setError(formatLockoutMessage(otpLock));
    } else if (step === 'credentials') {
      setError((prev) => (/too many failed otp attempts/i.test(prev) ? '' : prev));
    }
  }, [form.email, step]);

  useEffect(() => {
    lockoutTimerRef.current = setInterval(() => {
      const email = form.email.trim().toLowerCase();
      const now = Date.now();

      if (!otpLockedUntil) return;

      if (otpLockedUntil <= now) {
        if (email) clearOtpLockout(email);
        if (step === 'otp' || step === 'credentials') setError('');
      } else if (step === 'otp' || step === 'credentials') {
        setInfo('');
        setError(formatLockoutMessage(otpLockedUntil));
      }
    }, 1000);

    return () => {
      if (lockoutTimerRef.current) clearInterval(lockoutTimerRef.current);
    };
  }, [otpLockedUntil, step, form.email, clearOtpLockout]);
  */

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  /* OTP login — restore when email verification is re-enabled
  const handleOtpChange = (e) => {
    if (isOtpLocked) return;
    const value = e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH);
    setOtp(value);
  };
  */

  const clearMessages = () => {
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    setError('');
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) return showError('Please enter your email address.');
    if (!form.password) return showError('Please enter your password.');
    if (!form.role) return showError('Please select your role.');

    clearMessages();
    setLoading(true);
    try {
      const loggedInUser = await login(form.email, form.password, form.role);
      setFailedPasswordAttempts(0);
      const role = loggedInUser?.role || form.role;
      navigate(
        role === 'super_admin' ? '/companies'
          : role === 'bridge_engineer' ? '/bridges'
            : '/dashboard'
      );
    } catch (err) {
      const nextAttempts = failedPasswordAttempts + 1;
      setFailedPasswordAttempts(nextAttempts);
      if (nextAttempts >= MAX_FAILED_PASSWORD_ATTEMPTS) {
        navigate('/forgot-password', { state: { email: form.email.trim() } });
        return;
      }
      showError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  /* OTP login — restore when email verification is re-enabled
  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) return showError('Please enter your email address.');
    if (!form.password) return showError('Please enter your password.');
    if (!form.role) return showError('Please select your role.');

    const email = form.email.trim().toLowerCase();
    const existingLock = readStoredLockout(getOtpLockKey(email));
    if (existingLock) {
      setOtpLockedUntil(existingLock);
      setInfo('');
      setError(formatLockoutMessage(existingLock));
      setOtp('');
      setStep('otp');
      return;
    }

    clearMessages();
    setLoading(true);
    try {
      const data = await requestLoginOtp(form.email, form.password, form.role);
      clearOtpLockout(form.email);
      setFailedPasswordAttempts(0);
      setInfo(data?.message || `A verification code has been sent to ${maskEmail(form.email.trim())}.`);
      setOtp('');
      setStep('otp');
    } catch (err) {
      if (err.lockedUntil) {
        persistOtpLockout(form.email, err.lockedUntil);
        setStep('otp');
        return;
      }
      const nextAttempts = failedPasswordAttempts + 1;
      setFailedPasswordAttempts(nextAttempts);
      if (nextAttempts >= MAX_FAILED_PASSWORD_ATTEMPTS) {
        navigate('/forgot-password', { state: { email: form.email.trim() } });
        return;
      }
      showError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (isOtpLocked) return;
    if (otp.length !== OTP_LENGTH) {
      return showError('Please enter the 6-digit verification code.');
    }
    if (!isOtpLocked) clearMessages();
    setLoading(true);
    try {
      const loggedInUser = await verifyLoginOtp(form.email, otp);
      clearOtpLockout(form.email);
      const role = loggedInUser?.role || form.role;
      navigate(role === 'super_admin' ? '/companies' : '/dashboard');
    } catch (err) {
      setOtp('');
      handleOtpFailure(err, form.email);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (isOtpLocked) return;
    clearMessages();
    setLoading(true);
    try {
      const data = await requestLoginOtp(form.email, form.password, form.role, true);
      setInfo(data?.message || 'A new verification code has been sent to your email.');
      setOtp('');
    } catch (err) {
      if (err.lockedUntil) {
        persistOtpLockout(form.email, err.lockedUntil);
        return;
      }
      showError(err.message || 'Could not resend verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    clearMessages();
    setOtp('');
    setOtpLockedUntil(null);
    setStep('credentials');
  };
  */

  return (
    <div className="login-page">
      <div
        className="login-left"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/loginimage.jpeg)` }}
      >
        <div className="login-overlay" />
        <div className="login-logo">
          <AppLogo variant="default" className="login-logo-img" />
          <div className="login-logo-name">
            Indus Informatics Structural Monitoring©
          </div>
        </div>
        <div className="login-features">
          {[
            { icon: 'ti-radio', title: 'Real-time Monitoring', desc: 'Continuous data from multiple sensors' },
            { icon: 'ti-bell', title: 'Smart Alerts', desc: 'Intelligent notifications for critical events' },
            { icon: 'ti-chart-bar', title: 'Advanced Analytics', desc: 'Powerful insights for informed decisions' },
            { icon: 'ti-shield-check', title: 'Secure & Reliable', desc: 'Enterprise-grade security and data protection' },
          ].map((f) => (
            <div key={f.title} className="login-feature">
              <div className="login-feature-icon"><i className={`ti ${f.icon}`} /></div>
              <div>
                <h4>{f.title}</h4>
                <p>{f.desc}</p>
              </div>
            </div>
          ))}
          <div className="login-left-footer">
            <p className="login-left-security">
              <i className="ti ti-shield-check" />
              Your data is protected with enterprise-grade security
            </p>
            <p className="login-left-copyright">
              &copy; 2026 Indus Informatics. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      <div className="login-right">
        <div className="login-card">
          <h2>Welcome Back!</h2>
          <p className="login-subtitle">Sign in to access your monitoring dashboard</p>

          {error && <div className="login-error"><i className="ti ti-alert-circle" /> {error}</div>}

          <form onSubmit={handleCredentialsSubmit}>
            <div className="login-field">
              <label>Email Address</label>
              <div className="login-input-wrap">
                <i className="ti ti-mail" />
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email address"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="login-field">
              <label>Password</label>
              <div className="login-input-wrap">
                <i className="ti ti-lock" />
                <input
                  type={showPwd ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                />
                <button type="button" className="eye-btn" onClick={() => setShowPwd((p) => !p)}>
                  <i className={`ti ${showPwd ? 'ti-eye-off' : 'ti-eye'}`} />
                </button>
              </div>
            </div>

            <div className="login-field">
              <label>Select Role</label>
              <div className="login-input-wrap">
                <i className="ti ti-user-check" />
                <select name="role" value={form.role} onChange={handleChange}>
                  <option value="">-- Select your role --</option>
                  {LOGIN_ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="login-row">
              <label className="login-remember">
                <input type="checkbox" defaultChecked /> Remember me
              </label>
              <Link to="/forgot-password" className="login-forgot">Forgot Password?</Link>
            </div>

            <button type="submit" className="login-btn" disabled={loading}>
              {loading
                ? <><i className="ti ti-loader" /> Signing in...</>
                : <><i className="ti ti-login" /> Sign In</>}
            </button>
          </form>

          {/* OTP login UI — restore when email verification is re-enabled
          {step === 'credentials' ? (
            credentials form as above, with "Sending code..."
          ) : (
            <>
              <div className="login-icon-wrap">
                <i className="ti ti-mail-check" style={{ fontSize: 28, color: '#1a4fa8' }} />
              </div>
              <h2>Verify Your Email</h2>
              <p className="login-subtitle">
                Enter the 6-digit code sent to {maskEmail(form.email.trim())}
              </p>

              {error && <div className="login-error"><i className="ti ti-alert-circle" /> {error}</div>}
              {info && <div className="login-success"><i className="ti ti-circle-check" /> {info}</div>}

              <form onSubmit={handleOtpSubmit}>
                <div className="login-field">
                  <label htmlFor="login-otp">Verification Code</label>
                  <div className="login-input-wrap login-otp-wrap">
                    <i className="ti ti-shield-lock" />
                    <input
                      id="login-otp"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="one-time-code"
                      placeholder="Enter 6-digit code"
                      value={otp}
                      onChange={handleOtpChange}
                      maxLength={OTP_LENGTH}
                      className="login-otp-input"
                      disabled={loading || isOtpLocked}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="login-btn"
                  disabled={loading || isOtpLocked || otp.length !== OTP_LENGTH}
                >
                  {loading
                    ? <><i className="ti ti-loader" /> Verifying...</>
                    : <><i className="ti ti-check" /> Verify &amp; Sign In</>}
                </button>
              </form>

              <div className="login-otp-actions">
                <button
                  type="button"
                  className="login-otp-link"
                  onClick={handleResendOtp}
                  disabled={loading || isOtpLocked}
                >
                  Resend code
                </button>
                <button
                  type="button"
                  className="login-otp-link"
                  onClick={handleBackToCredentials}
                  disabled={loading}
                >
                  Back to sign in
                </button>
              </div>
            </>
          )}
          */}
        </div>
      </div>
    </div>
  );
}
